import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/context/AuthContext';
import { usePermission } from '@/shared/hooks/usePermission';
import { queryKeys } from '@/shared/query/keys';
import {
  getRailMenuItems,
  getVisibleRails,
  resolveWorkspaceStartPath,
  workspaceVisiblePages,
  type RailId,
} from '@/shared/layout/nav';
import { navWorkspaceAPI } from './navWorkspace.api';

/**
 * Where an org user lands after login and from `/`.
 * Super admin stays on `/platform`. A missing or blocked start page uses the dashboard.
 */
export const useOrgHomePath = (): { path: string; ready: boolean } => {
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = usePermission();
  const isOrgUser = isAuthenticated && user?.role !== 'super_admin';

  const query = useQuery({
    queryKey: queryKeys.navWorkspace.all,
    queryFn: () => navWorkspaceAPI.get(),
    enabled: Boolean(isOrgUser),
    staleTime: 60_000,
  });

  const path = useMemo(() => {
    if (user?.role === 'super_admin') return '/platform';
    const preference = query.data;
    const active = preference?.workspaces.find((entry) => entry.id === preference.activeId);
    const opts = {
      role: user?.role,
      hasPermission,
      entitlements: user?.entitlements,
    };
    const rails = getVisibleRails(opts);
    const items = {} as Partial<Record<RailId, ReturnType<typeof getRailMenuItems>>>;
    for (const rail of rails) {
      items[rail.id] = getRailMenuItems(rail.id, opts);
    }
    return resolveWorkspaceStartPath(active, workspaceVisiblePages(rails, items, active));
  }, [hasPermission, query.data, user?.entitlements, user?.role]);

  const ready = !isOrgUser || query.isSuccess || query.isError;
  return { path, ready };
};
